// src/features/notifications/ComposeNotificationPanel.tsx
//
// One compose flow covering three related "send a notification" RPCs,
// unified because the decision they represent is the same act — write a
// message, pick recipients — with only the timing/fan-out differing:
//   - one recipient, send now         -> SendNotification
//   - one recipient, scheduled        -> CreateNotificationScheduled
//   - multiple recipients, send now   -> SendNotificationsBulk
// (bulk + scheduled together isn't a single RPC — the schedule endpoint
// takes one notification — so scheduling is disabled once a second
// recipient line is added, and the panel says why.)

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Input, Textarea } from '@react/components';
import { Select } from '@/components/Select';
import { SegmentedControl } from '@/components/form';
import { useNotifications } from '@react/hooks';
import { NotificationChannelTypeValues } from '@core/types/notifications/NotificationChannelType';
import { NotificationPriorityValues } from '@core/types/notifications/NotificationPriority';
import { formatChannelType, formatPriority } from './logic';

export interface ComposeNotificationPanelProps {
  onClose: () => void;
  onSent: () => void;
}

export function ComposeNotificationPanel({ onClose, onSent }: ComposeNotificationPanelProps) {
  const { t } = useI18n();
  const store = useNotifications();
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [channelType, setChannelType] = useState('NOTIFICATION_CHANNEL_TYPE_EMAIL');
  const [priority, setPriority] = useState('NOTIFICATION_PRIORITY_NORMAL');
  const [scheduleAt, setScheduleAt] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recipientIds = recipients.split('\n').map(r => r.trim()).filter(Boolean);
  const isBulk = recipientIds.length > 1;

  const buildNotification = (recipientId: string) => ({
    subject: subject.trim(),
    content: content.trim(),
    channel_type: channelType as never,
    priority: priority as never,
    recipient: { entity: { type_id: '', type_name: 'profile', id: recipientId, platform_id: '', tenant_id: '', product_id: '' }, display_name: '', avatar: '' },
  });

  const handleSend = async () => {
    if (recipientIds.length === 0 || !subject.trim() || !content.trim()) return;
    setIsSending(true);
    setError(null);
    try {
      if (scheduleAt && !isBulk) {
        await store.CreateNotificationScheduled({
          notification: buildNotification(recipientIds[0]),
          scheduled_at: new Date(scheduleAt).toISOString() as never,
          allow_duplicate: false,
        } as never);
      } else if (isBulk) {
        await store.SendNotificationsBulk({
          notifications: { items: recipientIds.map(buildNotification) },
        } as never);
      } else {
        await store.SendNotification({ notification: buildNotification(recipientIds[0]) } as never);
      }
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unknown'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{t('notifications.compose.title')}</h3>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <Textarea
        label={t('notifications.compose.recipients')}
        helperText={t('notifications.compose.recipientsHint')}
        placeholder={t('notifications.compose.recipientsPlaceholder')}
        value={recipients}
        onChange={e => setRecipients(e.target.value)}
        rows={2}
      />
      <Input
        label={t('notifications.subject')}
        placeholder={t('notifications.compose.subjectPlaceholder')}
        value={subject}
        onChange={e => setSubject(e.target.value)}
      />
      <Textarea
        label={t('notifications.content')}
        placeholder={t('notifications.compose.contentPlaceholder')}
        value={content}
        onChange={e => setContent(e.target.value)}
        rows={3}
      />

      <div className="flex flex-wrap items-center gap-4">
        <div>
          <label className="label">{t('notifications.channelType')}</label>
          <Select value={channelType} onChange={e => setChannelType(e.target.value)} fullWidth={false} className="w-40">
            {NotificationChannelTypeValues.filter(v => v !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED').map(v => (
              <option key={v} value={v}>{t(formatChannelType(v).labelKey)}</option>
            ))}
          </Select>
        </div>
        <div>
          <label className="label">{t('notifications.priority')}</label>
          <SegmentedControl
            aria-label={t('notifications.priority')}
            size="sm"
            value={priority}
            onChange={setPriority}
            options={NotificationPriorityValues.filter(v => v !== 'NOTIFICATION_PRIORITY_UNSPECIFIED').map(v => ({
              value: v,
              label: t(formatPriority(v).labelKey),
            }))}
          />
        </div>
      </div>

      <Input
        label={t('notifications.compose.scheduleFor')}
        type="datetime-local"
        value={scheduleAt}
        onChange={e => setScheduleAt(e.target.value)}
        disabled={isBulk}
        helperText={isBulk ? t('notifications.compose.bulkNoSchedule') : undefined}
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex gap-2">
        <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSend}
          isLoading={isSending}
          disabled={recipientIds.length === 0 || !subject.trim() || !content.trim()}
        >
          {scheduleAt && !isBulk ? t('notifications.compose.schedule') : t('notifications.compose.send')}
        </Button>
      </div>
    </div>
  );
}

export default ComposeNotificationPanel;
